import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import DataTable from '@/components/admin/DataTable';
import {
  ConfirmDeleteDialog,
  InlineSelect,
  StatusBadge,
  courseLabel,
  courseStatusOptions,
  labelize,
} from '@/components/admin/AcademicTableHelpers';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  bulkActionCourses,
  createCourse,
  deleteCourse,
  getCourseTerms,
  getCourses,
  getInstructors,
  getPrograms,
  updateCourse,
} from '@/lib/api';
import {
  buildProgramAbbreviationMap,
  courseSubtitle,
  summarizeCoursePrograms,
} from '@/lib/courseSummary';
import { useAuth } from '@/contexts/AuthContext';
import { Archive, BookOpen, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react';

const allOption = { value: 'all', label: 'All' };

function filterParam(value) {
  return value === 'all' ? undefined : value;
}

export default function ManageCourses() {
  const { loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [terms, setTerms] = useState([]);
  const [programAbbreviations, setProgramAbbreviations] = useState(() => new Map());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [termFilter, setTermFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [savingInline, setSavingInline] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  // Which fields failed validation, so the message can sit on the field.
  const [errors, setErrors] = useState({});
  const [editingCourse, setEditingCourse] = useState(null);
  const loadRequestId = useRef(0);
  const [form, setForm] = useState({
    title: '',
    description: '',
    type: 'self-paced',
    category: 'General',
    status: 'draft',
    instructor_id: '',
    instructor_name: '',
    term: '',
    course_code: '',
    credits: 3,
    hours: 0,
  });

  const loadSupportData = async () => {
    try {
      const [courseRes, instructorRes, termRes, programRes] = await Promise.all([
        getCourses(),
        getInstructors(),
        getCourseTerms(),
        // Programs only supply the abbreviations for the list badges, so a
        // failure here should not take the whole page down with it.
        getPrograms().catch(() => ({ data: [] })),
      ]);
      setAllCourses(courseRes.data || []);
      setInstructors(instructorRes.data || []);
      setTerms(termRes.data || []);
      setProgramAbbreviations(buildProgramAbbreviationMap(programRes.data || []));
    } catch (err) {
      console.error(err);
      toast.error('Could not load course filters');
    }
  };

  const loadCourses = async (requestId = ++loadRequestId.current, searchValue = search) => {
    if (requestId !== loadRequestId.current) return;
    try {
      setLoading(true);
      const params = {
        search: searchValue.trim() || undefined,
        status: filterParam(statusFilter),
        category: filterParam(categoryFilter),
        type: filterParam(typeFilter),
        term: filterParam(termFilter),
      };
      const { data } = await getCourses(params);
      if (requestId !== loadRequestId.current) return;
      const nextCourses = data || [];
      setCourses(nextCourses);
      setSelectedIds((prev) => {
        const visible = new Set(nextCourses.map((course) => course.id));
        return new Set([...prev].filter((id) => visible.has(id)));
      });
    } catch (err) {
      if (requestId !== loadRequestId.current) return;
      console.error(err);
      toast.error('Could not load courses');
    } finally {
      if (requestId === loadRequestId.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) {
      loadSupportData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading]);

  useEffect(() => {
    if (!authLoading) {
      const requestId = ++loadRequestId.current;
      setLoading(true);
      const timeoutId = window.setTimeout(() => loadCourses(requestId, search), 300);
      return () => window.clearTimeout(timeoutId);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, search, statusFilter, categoryFilter, typeFilter, termFilter]);

  const selectedCourses = useMemo(
    () => courses.filter((course) => selectedIds.has(course.id)),
    [courses, selectedIds],
  );

  const categoryOptions = useMemo(() => {
    const values = [...new Set(allCourses.map((course) => course.category).filter(Boolean))].sort();
    return [allOption, ...values.map((value) => ({ value, label: value }))];
  }, [allCourses]);

  const typeOptions = useMemo(() => {
    const values = [...new Set(allCourses.map((course) => course.type).filter(Boolean))].sort();
    return [allOption, ...values.map((value) => ({ value, label: labelize(value) }))];
  }, [allCourses]);

  const termOptions = useMemo(
    () => [allOption, ...terms.map((term) => ({ value: term, label: term }))],
    [terms],
  );

  const openCreate = () => {
    setEditingCourse(null);
    setForm({
      title: '',
      description: '',
      type: 'self-paced',
      category: 'General',
      status: 'draft',
      instructor_id: '',
      instructor_name: '',
      term: '',
      course_code: '',
      credits: 3,
      hours: 0,
    });
    setShowDialog(true);
  };

  const openEdit = (course) => {
    setEditingCourse(course);
    setForm({
      title: course.title || '',
      description: course.description || '',
      type: course.type || 'self-paced',
      category: course.category || 'General',
      status: course.status || 'draft',
      instructor_id: course.instructor_id || '',
      instructor_name: course.instructor_name || '',
      term: course.term || '',
      course_code: course.course_code || '',
      credits: course.credits ?? 3,
      hours: course.hours ?? 0,
    });
    setShowDialog(true);
  };

  const refreshAfterMutation = async () => {
    await Promise.all([loadCourses(), loadSupportData()]);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      // Beside the field as well as in the toast — the toast is gone in two
      // seconds and never says which field it meant.
      setErrors({ title: 'Give the course a title.' });
      toast.error('Title is required');
      return;
    }
    setErrors({});
    try {
      if (editingCourse) {
        await updateCourse(editingCourse.id, form);
        toast.success('Course updated');
        setShowDialog(false);
        await refreshAfterMutation();
        return;
      }
      // A new course has no content yet, so drop straight into it rather than
      // returning to the list — building it out is always the next step.
      const { data } = await createCourse(form);
      toast.success('Course created — add your modules and lessons');
      setShowDialog(false);
      if (data?.id) {
        navigate(`/admin/courses/${data.id}`);
        return;
      }
      await refreshAfterMutation();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to save course');
    }
  };

  const handleInlineStatus = async (course, status) => {
    try {
      setSavingInline(course.id);
      const { data } = await updateCourse(course.id, { status });
      setCourses((prev) => prev.map((item) => (item.id === course.id ? data : item)));
      setAllCourses((prev) => prev.map((item) => (item.id === course.id ? data : item)));
      toast.success(`Course marked ${labelize(status).toLowerCase()}`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not update course status');
      await loadCourses();
    } finally {
      setSavingInline('');
    }
  };

  const confirmDelete = (items, type = 'single') => {
    setPendingDelete({ type, courses: items });
  };

  const handleConfirmedDelete = async () => {
    if (!pendingDelete) return;
    const ids = pendingDelete.courses.map((course) => course.id);
    try {
      setDeleting(true);
      if (pendingDelete.type === 'bulk') {
        const { data } = await bulkActionCourses({ ids, action: 'delete' });
        toast.success(data?.message || `${ids.length} courses deleted`);
      } else {
        await deleteCourse(ids[0]);
        toast.success('Course deleted');
      }
      setPendingDelete(null);
      setSelectedIds(new Set());
      await refreshAfterMutation();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not delete course records');
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkStatus = async (action) => {
    if (selectedCourses.length === 0) return;
    try {
      const ids = selectedCourses.map((course) => course.id);
      const { data } = await bulkActionCourses({ ids, action });
      toast.success(data?.message || `${ids.length} courses updated`);
      setSelectedIds(new Set());
      await refreshAfterMutation();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Bulk action failed');
    }
  };

  // Eleven columns — several of them long free text — stretched the table far
  // past the viewport and pushed the row actions off-screen. Related facts are
  // now packed into one cell each: identity and academic load under the title,
  // delivery as two short badges, and programs as abbreviation badges with the
  // full names on hover.
  const columns = [
    {
      id: 'title',
      header: 'Course',
      sortable: true,
      sortAccessor: (course) => course.title || '',
      cellClassName: 'min-w-[280px] max-w-[380px]',
      cell: (course) => (
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#062B1A]/5">
            <BookOpen className="h-4 w-4 text-[#062B1A]" />
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-[#062B1A]" title={course.title || ''}>
              {course.title || 'Untitled course'}
            </p>
            <p className="truncate text-xs text-[#062B1A]/45">{courseSubtitle(course)}</p>
          </div>
        </div>
      ),
    },
    {
      id: 'category',
      header: 'Delivery',
      sortable: true,
      sortAccessor: (course) => course.category || '',
      cellClassName: 'min-w-[150px]',
      cell: (course) => (
        <div className="flex flex-col items-start gap-1">
          <Badge variant="outline" className="border-[#D4AF37]/30 bg-[#D4AF37]/5 text-[#8A6508]">
            {course.category || 'General'}
          </Badge>
          <span className="text-xs text-[#062B1A]/50">{labelize(course.type)}</span>
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      cellClassName: 'min-w-[150px]',
      cell: (course) => (
        <InlineSelect
          value={course.status || 'draft'}
          disabled={savingInline === course.id}
          options={courseStatusOptions}
          onChange={(value) => handleInlineStatus(course, value)}
        />
      ),
    },
    {
      id: 'instructor',
      header: 'Instructor',
      sortable: true,
      sortAccessor: (course) => course.instructor_name || '',
      cellClassName: 'min-w-[150px] max-w-[180px] text-sm text-[#062B1A]/70',
      cell: (course) => (
        <span className="block truncate" title={course.instructor_name || ''}>
          {course.instructor_name || '-'}
        </span>
      ),
    },
    {
      id: 'enrollment_count',
      header: 'Enrolled',
      sortable: true,
      sortAccessor: (course) => Number(course.enrollment_count || 0),
      cellClassName: 'min-w-[90px] text-sm font-medium text-[#062B1A]',
      cell: (course) => course.enrollment_count || 0,
    },
    {
      id: 'programs',
      header: 'Programs',
      cellClassName: 'min-w-[150px]',
      cell: (course) => {
        const { badges, overflow, title } = summarizeCoursePrograms(
          course.program_names,
          programAbbreviations
        );
        if (!badges.length) return <span className="text-sm text-[#062B1A]/35">-</span>;
        return (
          <div className="flex flex-wrap items-center gap-1" title={title}>
            {badges.map((badge) => (
              <Badge
                key={badge.name}
                variant="outline"
                className="border-[#062B1A]/15 bg-[#F7F8FA] text-[11px] text-[#062B1A]/70"
              >
                {badge.label}
              </Badge>
            ))}
            {overflow > 0 && (
              <span className="text-[11px] font-medium text-[#062B1A]/45">+{overflow}</span>
            )}
          </div>
        );
      },
    },
    {
      id: 'term',
      header: 'Term',
      sortable: true,
      cellClassName: 'min-w-[120px]',
      cell: (course) => (course.term ? <StatusBadge value={course.term} /> : <span className="text-sm text-[#062B1A]/35">-</span>),
    },
  ];

  const openCourse = (course) => navigate(`/admin/courses/${course.id}`);

  // "Open" is the primary action and is filled, so it reads as the way into a
  // course. "Edit" only changes the course's settings (title, code, credits) —
  // it is deliberately secondary, since clicking a course should show its
  // content, not a settings form.
  const rowActions = (course) => (
    <div className="flex justify-end gap-2">
      <Button
        type="button"
        size="sm"
        className="bg-[#062B1A] text-white hover:bg-[#041B10]"
        onClick={() => openCourse(course)}
      >
        <BookOpen className="w-4 h-4 mr-2" />
        Open
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="border-[#062B1A]/15 text-[#062B1A]"
        onClick={() => openEdit(course)}
      >
        <Pencil className="w-4 h-4 mr-2" />
        Settings
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
        onClick={() => confirmDelete([course])}
      >
        <Trash2 className="w-4 h-4 mr-2" />
        Delete
      </Button>
    </div>
  );

  const bulkActions = ({ selectedCount }) => (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={selectedCount === 0}
        className="border-green-200 text-green-700 hover:bg-green-50"
        onClick={() => handleBulkStatus('publish')}
      >
        <RotateCcw className="w-4 h-4 mr-2" />
        Publish
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={selectedCount === 0}
        className="border-[#D4AF37]/40 text-[#8A6508] hover:bg-[#D4AF37]/5"
        onClick={() => handleBulkStatus('archive')}
      >
        <Archive className="w-4 h-4 mr-2" />
        Archive
      </Button>
      <Button
        type="button"
        size="sm"
        disabled={selectedCount === 0}
        className="bg-red-700 text-white hover:bg-red-800"
        onClick={() => confirmDelete(selectedCourses, 'bulk')}
      >
        <Trash2 className="w-4 h-4 mr-2" />
        Bulk Delete
      </Button>
    </>
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
            Manage Courses
          </h2>
          <p className="text-sm text-[#062B1A]/60 mt-1">
            {courses.length} course{courses.length === 1 ? '' : 's'} in the current view
          </p>
        </div>
        <Button className="bg-[#062B1A] text-white hover:bg-[#041B10] btn-hover" onClick={openCreate} data-testid="admin-courses-create-button">
          <Plus className="w-4 h-4 mr-2" />
          Create Course
        </Button>
      </div>

      <DataTable
        title="Course Management"
        description="Click a course to open its content. Use Settings to change its details, or remove courses with cascade cleanup."
        data={courses}
        columns={columns}
        loading={loading}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search courses..."
        searchHint="Search by title, course code, description, category, instructor, or term."
        filters={[
          { id: 'status', value: statusFilter, onChange: setStatusFilter, options: [{ value: 'all', label: 'All statuses' }, ...courseStatusOptions] },
          { id: 'category', value: categoryFilter, onChange: setCategoryFilter, options: categoryOptions.map((option) => option.value === 'all' ? { ...option, label: 'All categories' } : option) },
          { id: 'type', value: typeFilter, onChange: setTypeFilter, options: typeOptions.map((option) => option.value === 'all' ? { ...option, label: 'All types' } : option) },
          { id: 'term', value: termFilter, onChange: setTermFilter, options: termOptions.map((option) => option.value === 'all' ? { ...option, label: 'All terms' } : option) },
        ]}
        selectedIds={selectedIds}
        onSelectedIdsChange={setSelectedIds}
        bulkActions={bulkActions}
        rowActions={rowActions}
        onRowClick={openCourse}
        emptyMessage="No courses match the current filters."
        pageSize={8}
      />

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>
              {editingCourse ? 'Edit Course' : 'Create Course'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label htmlFor="course-title" className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Title *</label>
              <Input
                id="course-title"
                value={form.title}
                onChange={(event) => {
                  setForm((prev) => ({ ...prev, title: event.target.value }));
                  if (errors.title) setErrors((prev) => ({ ...prev, title: '' }));
                }}
                placeholder="Course title"
                aria-invalid={errors.title ? 'true' : undefined}
                aria-describedby={errors.title ? 'course-title-error' : undefined}
                className={errors.title ? 'border-rose-400 focus-visible:ring-rose-400' : 'border-[rgba(6,43,26,0.12)]'}
              />
              {errors.title && (
                <p id="course-title-error" role="alert" className="mt-1 text-xs font-medium text-rose-600">{errors.title}</p>
              )}
            </div>
            <div>
              <label htmlFor="course-description" className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Description</label>
              <Textarea id="course-description" value={form.description} onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))} placeholder="Course description" className="border-[rgba(6,43,26,0.12)]" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div>
                <label htmlFor="course-term" className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Term</label>
                <Input id="course-term" value={form.term} onChange={(event) => setForm((prev) => ({ ...prev, term: event.target.value }))} placeholder="Fall 2026" className="border-[rgba(6,43,26,0.12)]" />
              </div>
              <div>
                <label htmlFor="course-code" className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Course Code</label>
                <Input id="course-code" value={form.course_code} onChange={(event) => setForm((prev) => ({ ...prev, course_code: event.target.value }))} placeholder="MGMT 338" className="border-[rgba(6,43,26,0.12)]" />
              </div>
              <div>
                <label htmlFor="course-credits" className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Credits</label>
                <Input id="course-credits" type="number" min={0} value={form.credits} onChange={(event) => setForm((prev) => ({ ...prev, credits: parseInt(event.target.value, 10) || 0 }))} className="border-[rgba(6,43,26,0.12)]" />
              </div>
              <div>
                <label htmlFor="course-hours" className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Hours</label>
                <Input id="course-hours" type="number" min={0} value={form.hours} onChange={(event) => setForm((prev) => ({ ...prev, hours: parseInt(event.target.value, 10) || 0 }))} className="border-[rgba(6,43,26,0.12)]" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="course-type" className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Type</label>
                <Select value={form.type} onValueChange={(value) => setForm((prev) => ({ ...prev, type: value }))}>
                  <SelectTrigger id="course-type" className="border-[rgba(6,43,26,0.12)]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self-paced">Self-Paced</SelectItem>
                    <SelectItem value="live">Live</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label htmlFor="course-category" className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Category</label>
                <Input id="course-category" value={form.category} onChange={(event) => setForm((prev) => ({ ...prev, category: event.target.value }))} placeholder="Programming" className="border-[rgba(6,43,26,0.12)]" />
              </div>
            </div>
            <div>
              <label htmlFor="course-instructor" className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Instructor</label>
              <Select
                value={form.instructor_id || '_none'}
                onValueChange={(value) => {
                  const instructorId = value === '_none' ? '' : value;
                  const instructor = instructors.find((item) => item.id === instructorId);
                  setForm((prev) => ({ ...prev, instructor_id: instructorId, instructor_name: instructor?.name || '' }));
                }}
              >
                <SelectTrigger id="course-instructor" className="border-[rgba(6,43,26,0.12)]"><SelectValue placeholder="Select instructor" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">No instructor</SelectItem>
                  {instructors.map((instructor) => (
                    <SelectItem key={instructor.id} value={instructor.id}>{instructor.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label htmlFor="course-status" className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Status</label>
              <Select value={form.status} onValueChange={(value) => setForm((prev) => ({ ...prev, status: value }))}>
                <SelectTrigger id="course-status" className="border-[rgba(6,43,26,0.12)]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {courseStatusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button className="bg-[#D4AF37] text-black hover:bg-[#B8941F]" onClick={handleSave}>
              {editingCourse ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={`Delete ${pendingDelete?.courses?.length || 0} course${pendingDelete?.courses?.length === 1 ? '' : 's'}?`}
        description={`This permanently removes the selected course${pendingDelete?.courses?.length === 1 ? '' : 's'} and related lessons, exams, assignments, and live sessions.`}
        count={pendingDelete?.courses?.length || 0}
        deleting={deleting}
        onConfirm={handleConfirmedDelete}
      />
    </div>
  );
}
